import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { authFetch, dataFetch } from '../../api';
import PageHeader from '../../components/ui/PageHeader';

const { FiLock, FiUser, FiSave, FiCheckCircle, FiAlertCircle } = FiIcons;

const EmployeeSettings = () => {
  const { currentUser, login } = useAuth();
  const [name, setName] = useState(currentUser?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [profileStatus, setProfileStatus] = useState(null);
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoadingProfile(true);
    setProfileStatus(null);
    try {
      // Update better-auth user
      const res = await authFetch('update-user', {
        method: 'POST',
        body: JSON.stringify({ name })
      });
      
      if (res?.status === 'success' || res?.user) {
        setProfileStatus({ type: 'success', message: 'Profile updated successfully. Your avatar initials have been updated.' });
        // Update employee record if exists to stay synchronized
        const empRes = await dataFetch(`read/employees?user_id=${currentUser.id}`);
        if (empRes?.data?.length > 0) {
          await dataFetch(`update/employees/${empRes.data[0].id}`, {
            method: 'PUT',
            body: JSON.stringify({ ...empRes.data[0], name })
          });
        }
        // Refresh session
        const sessionRes = await authFetch('get-session');
        if (sessionRes?.data?.user) {
          login(sessionRes.data.user);
        }
      } else {
        setProfileStatus({ type: 'error', message: res?.error || 'Failed to update profile.' });
      }
    } catch (err) {
      setProfileStatus({ type: 'error', message: 'Network error.' });
    }
    setLoadingProfile(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'New passwords do not match.' });
      return;
    }
    setLoadingPassword(true);
    setPasswordStatus(null);
    try {
      const res = await authFetch('change-password', {
        method: 'POST',
        body: JSON.stringify({ newPassword, currentPassword, revokeOtherSessions: true })
      });
      if (res?.status === 'success' || res?.message) {
        setPasswordStatus({ type: 'success', message: 'Password changed successfully.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordStatus({ type: 'error', message: res?.error || 'Failed to change password. Please check your current password.' });
      }
    } catch (err) {
      setPasswordStatus({ type: 'error', message: 'Network error.' });
    }
    setLoadingPassword(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-4xl mx-auto pb-20">
      <PageHeader 
        eyebrow="Account" 
        title="My Settings" 
        description="Manage your profile, avatar, and security settings." 
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profile / Avatar Settings */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
            <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiUser} className="text-blue-600 text-sm" />
            </div>
            <h3 className="text-sm font-semibold text-slate-800">Profile & Avatar</h3>
          </div>
          <div className="p-5 flex-1 space-y-6">
            <div className="flex flex-col items-center justify-center space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="w-20 h-20 rounded-full bg-[#1A233A] flex items-center justify-center text-[#00E5FF] font-bold text-3xl shadow-inner">
                {currentUser?.avatar}
              </div>
              <p className="text-xs text-slate-500 text-center">Your avatar is generated automatically from your initials.</p>
            </div>

            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name</label>
                <input 
                  type="text" 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              
              {profileStatus && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-xs font-medium ${profileStatus.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  <SafeIcon icon={profileStatus.type === 'success' ? FiCheckCircle : FiAlertCircle} />
                  {profileStatus.message}
                </div>
              )}

              <button 
                type="submit" 
                disabled={loadingProfile}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-70"
              >
                {loadingProfile ? 'Saving...' : <><SafeIcon icon={FiSave} /> Save Profile</>}
              </button>
            </form>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
            <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiLock} className="text-amber-600 text-sm" />
            </div>
            <h3 className="text-sm font-semibold text-slate-800">Security</h3>
          </div>
          <div className="p-5 flex-1">
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Current Password</label>
                <input 
                  type="password" 
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">New Password</label>
                <input 
                  type="password" 
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm New Password</label>
                <input 
                  type="password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                  required
                  minLength={8}
                />
              </div>

              {passwordStatus && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-xs font-medium ${passwordStatus.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  <SafeIcon icon={passwordStatus.type === 'success' ? FiCheckCircle : FiAlertCircle} />
                  {passwordStatus.message}
                </div>
              )}

              <button 
                type="submit" 
                disabled={loadingPassword}
                className="w-full flex items-center justify-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-70 mt-2"
              >
                {loadingPassword ? 'Updating...' : <><SafeIcon icon={FiLock} /> Update Password</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EmployeeSettings;